<template>
    <div id="tradeTrackerForm">

        <div class="border border-t-0 mt-4 mb-4">
            <div class="flex flex-wrap bg-tan-200 border-t-2 border-blue-600 px-3 pt-2 md:pb-0 pb-2">
                <div class="md:flex-1 flex flex-wrap w-full pr-3 xs:pr-0 xs:pb-2">
                    <label class="inline-block self-center mr-4 mb-2 xs:w-full flex-grow relative">
                        <div class="tooltip-error" v-show="newPortfolio.error">{{ newPortfolio.error }}</div>
                        <input @input="newPortfolioInput" :class="{'is-invalid': newPortfolio.error}" class="form-input-inline-w-none form-input-sm-inline w-full" type="text" v-model="newPortfolio.caption" maxlength="40" placeholder="Enter a title..." aria-label="Enter a title" />
                    </label>
                    <label for="cashValue" class="inline-block self-center mr-4 mb-2 xs:w-full">
                        Starting Cash Value:
                        <div class="inline-block xs:w-full">
                            <input id="cashValue" class="form-input-inline-w-none form-input-sm-inline w-full" type="text" v-model="newPortfolio.cash" placeholder="1000.00 or 1,000.00" aria-label="Cash" />
                        </div>
                    </label>
                    <div class="inline-block xs:w-full">
                        <label for="summaryExclude" class="inline-block self-center mb-2 mr-2 xs:w-full">
                            <div class="inline-block">
                                <input id="summaryExclude" type="checkbox" name="summary_exclude" :true-value="'1'" :false-value="'0'" v-model="newPortfolio.summary_exclude" aria-label="Summary Exclude">
                            </div>
                            Exclude from the All Portfolios (Summary) View
                        </label>
                        <label for="adjustCash" class="inline-block self-center mb-2 mr-2 xs:w-full">
                            <div class="inline-block">
                                <input id="adjustCash" type="checkbox" name="adjust_cash" :true-value="'1'" :false-value="'0'" v-model="newPortfolio.adjust_cash" aria-label="Adjust Cash">
                            </div>
                            Cash Adjustment
                        </label>
                    </div>
                </div>
                <div class="">
                    <span class="btn btn-sm btn-yellow inline-block" @click="addPortfolio">
                        &nbsp;Add Portfolio&nbsp;
                    </span>
                </div>
            </div>
        </div>

        <div class="relative border-t-2 border-blue-600 mt-4">
            <div v-show="dataLoading" class="absolute z-50 w-full h-full bg-spinner-ghost flex flex-col items-center">
                <spinner class="mb-1"></spinner>
            </div>

            <div class="table-header border-b">
                <div class="flex flex-wrap w-full">
                    <div class="font-bold">Existing Portfolios</div>
                    <div class="justify-center ml-auto">
                        <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1 xs:ml-0" @click="close">Cancel</div>
                        <div class="btn btn-sm btn-yellow inline-flex trade-tracker-form-save" @click="save">Save</div>
                    </div>
                </div>
            </div>

            <div class="table-responsive">
                <table class="table table-striped table-hover border border-t-0">
                    <thead>
                        <tr>
                            <th class="w-16 text-center">Sort</th>
                            <th>Portfolio Title</th>
                            <th class="xs:hidden">Cash Value</th>
                            <th class="xs:hidden">Summary Exclude</th>
                            <th>Cash Adjustment</th>
                            <th v-if="jims">Public</th>
                            <th class="w-24 text-center">Delete</th>
                        </tr>
                    </thead>
                    <draggable v-model="portfolios" tag="tbody" handle=".canDrag" item-key="id">
                        <template #item="{ element, index }">
                            <tr>
                                <td class="text-center cursor-move canDrag">
                                    <i class="fas fa-arrows-alt text-gray-500"></i>
                                </td>
                                <td>
                                    <div class="error" v-show="element.error">{{ element.error }}</div>
                                    <input :class="{'is-invalid': element.error}" class="form-input-inline-w-none form-input-sm-inline w-full" type="text" v-model="element.caption" maxlength="40" aria-label="Caption" />
                                </td>
                                <td class="xs:hidden">
                                    {{ toNumeric(element.cash) }}
                                </td>
                                <td class="xs:hidden">
                                    {{ element.summary_exclude ? 'Yes' : 'No' }}
                                </td>
                                <td>
                                    {{ element.adjust_cash ? 'Yes' : 'No' }}
                                </td>
                                <td v-if="jims">
                                    <input id="portfolioPublic" type="checkbox" name="public" :true-value="'1'" :false-value="'0'" v-model="element.public">
                                </td>
                                <td class="text-center">
                                    <a v-if="portfolios.length > 1" @click="removePortfolio(index)" v-tooltip="{ content: 'Delete Current Portfolio', placement: 'auto-start', theme: 'red' }"  class="btn btn-default btn-shadow-none btn-red-ghost btn-xs ml-1 mb-1 md:mb-0" title="Delete Portfolio">
                                        <i class="fas fa-times"></i>
                                    </a>
                                </td>
                            </tr>
                        </template>
                    </draggable>
                    <tfoot>
                        <tr>
                            <th class="text-center">Sort</th>
                            <th>Portfolio Title</th>
                            <th class="xs:hidden">Cash Value</th>
                            <th class="xs:hidden">Summary Exclude</th>
                            <th>Cash Adjustment</th>
                            <th v-if="jims">Public</th>
                            <th class="w-24 text-center">Delete</th>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>

        <div class="flex justify-center mt-4">
            <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1 xs:ml-0" @click="close">Cancel</div>
            <div class="btn btn-sm btn-yellow inline-flex mx-1 trade-tracker-form-save" @click="save">Save</div>
        </div>

    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import Draggable from 'vuedraggable';
const props = defineProps({
    initialPortfolios: {
        type: Array,
        required: true
    },
    jims: Boolean
});

const emit = defineEmits();

const views = ref([
    { label: 'Portfolio', value: 'portfolio', default: false },
    { label: 'Quote Board', value: 'main', default: false }
]);
const dataLoading = ref(false);
const portfolios = ref(JSON.parse(JSON.stringify(props.initialPortfolios)));
const newPortfolio = ref({
    caption: '',
    cash: '',
    summary_exclude: '',
    adjust_cash: '',
    error: '',
});

const hasChanged = () => {
    return (JSON.stringify(props.initialPortfolios) !== JSON.stringify(portfolios.value)) || newPortfolio.value.caption;
};

const toNumeric = (number, decimals = 2) => {
    let value = number || 0;
    if (typeof value === 'string') {
        if (value.indexOf(',') !== -1) {
            value = value.replace(/[,]/g, "");
        }
        return Number(Math.round(value+'e'+decimals)+'e-'+decimals).toFixed(decimals);
    }
    return value.toFixed(decimals);
};

const newPortfolioInput = () => {
    newPortfolio.value.error='';
    let saveButtons = document.getElementsByClassName('trade-tracker-form-save');
    if (newPortfolio.value.caption) {
        [...saveButtons].forEach((el) => {
            el.classList.add('btn-disabled');
        });
    } else {
        [...saveButtons].forEach((el) => {
            el.classList.remove('btn-disabled');
        });
    }
};

const getViewsOptions = (selectedOption) => {
    var mViews = JSON.parse(JSON.stringify(views.value));
    return mViews.map(option => {
        if (option.value == selectedOption) {
            option.default = true;
        } else {
            option.default = false;
        }
        return option;
    });
};

const close = () => {
    emit('close');
};

const validate = () => {
    let valid = true;

    portfolios.value.map((mPortfolio) => {
        if (mPortfolio.caption === '') {
            mPortfolio.error = 'Please enter a portfolio title';
            valid = false;
        }
    });

    return valid;
};

const save = () => {
    dataLoading.value = true;
    if (!validate()) {
        dataLoading.value = false;
        return;
    }

    // get ids list to be removed
    const initialPortfolioIds = props.initialPortfolios.filter(c => c.id != undefined);
    const portfolioIds = portfolios.value.filter(c => c.id != undefined);
    const removePortfolios =
        [initialPortfolioIds, portfolioIds]
        .sort(
            (a, b) => b.length - a.length
        )
        .reduce(
            (a, b) => a.filter(
                o => !b.some(
                    v => v.id === o.id
                )
            )
        )
        .map(x => x.id);

    const route = window.route('api.trade-tracker.update-batch');
    axios.post(route, {
        portfolios: portfolios.value,
        removePortfolios: removePortfolios,
    })
    .then((response) => {
        emit('save', response.data.data);
        dataLoading.value = false;
    }).catch((error) => {
        console.error(error);
        dataLoading.value = false;
    })
};

const addPortfolio = () => {
    dataLoading.value = true;
    setTimeout(()=> {
        dataLoading.value = false;
        if (newPortfolio.value.caption == '') {
            newPortfolio.value.error = 'Please enter a title...';
            return;
        }

        portfolios.value.push({
            caption: newPortfolio.value.caption,
            cash: newPortfolio.value.cash || '',
            summary_exclude: newPortfolio.value.summary_exclude || 0,
            adjust_cash: newPortfolio.value.adjust_cash || 0,
            default_view: 'portfolio',
        })

        newPortfolio.value = {
            caption: '',
            cash: '',
            summary_exclude: '',
            adjust_cash: '',
            error: '',
        };

        newPortfolioInput();

    }, 150);
};

const removePortfolio = (index) => {
    portfolios.value.splice(index, 1);
};

const handleLeave = (e) => {
    if (hasChanged()) {
        e.preventDefault();
        e.returnValue = 'Are you sure?';
        return 'Are you sure?';
    }
};

onMounted(() => {
    window.addEventListener('beforeunload', handleLeave);
});

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleLeave);
});
</script>

<style lang="scss">
#tradeTrackerForm {
    .tooltip-error {
        @apply absolute z-50 bg-red-500 text-white text-xs font-normal px-2 py-1 -mt-7 border-red-500 border-b rounded;
    }
    table {
        thead,
        tfoot {
            td, th {
                @apply px-3 py-2 text-center;
            }
        }
        tbody {
            td, th {
                @apply px-3 text-center;
            }
        }
        .sortable {
            @apply cursor-pointer;
        }
    }
}
</style>
